import { FunctionComponent, memo } from "react";
import { IProps } from "./confirmation-popover.types";
import { Popover } from "../popover";
import { Stack } from "../stack";
import { Text } from "../text";
import { Group } from "../group";
import { Button } from "../button";

/**
 * A reusable confirmation popover: renders its `children` as the trigger and, once `opened`, a small
 * dropdown asking the user to confirm or cancel an action. Useful anywhere an action needs a
 * lightweight "are you sure?" step without the weight of a full modal.
 * `withinPortal` defaults to `false`, and that default is load-bearing rather than incidental: keeping
 * the dropdown out of a portal is what lets this popover be nested inside ANOTHER popover, with its
 * trigger sitting inside that outer popover's dropdown. Portalled, the dropdown's DOM node renders
 * outside the outer popover's subtree, so the outer popover's click-outside detection stops recognising
 * a click in here as "inside" and dismisses itself the moment the user reaches for Confirm or Cancel.
 * Only pass `true` for a standalone usage that is never nested inside another popover.
 *
 * Note the dropdown sets its own padding: the shared `Popover` wrapper zeroes it, so a consumer that
 * relies on the default gets copy flush against the border.
 * @param props.children         The trigger element that opens/controls the popover, e.g. a "Done" button.
 *                               Must be a single element, not arbitrary `ReactNode` — Mantine's
 *                               `Popover.Target` clones its child to attach a ref, so a string or
 *                               fragment compiles but throws at runtime.
 * @param props.opened           Whether the confirmation dropdown is shown.
 * @param props.message          The confirmation message shown inside the dropdown.
 * @param props.onConfirm        Called when the user confirms the action.
 * @param props.onCancel         Called when the user cancels the action. Does not affect anything
 *                               outside this popover — in particular it must not close an enclosing one.
 * @param props.onDismiss        Called when the popover is dismissed without an explicit choice
 *                               (e.g. click outside, escape).
 * @param props.confirmLabel     Label for the confirm button. Defaults to "Continue".
 * @param props.cancelLabel      Label for the cancel button. Defaults to "Cancel".
 * @param props.position         Floating position of the dropdown relative to the trigger. Defaults to "bottom-end".
 * @param props.withinPortal     Whether the dropdown renders inside a React portal. Defaults to `false`; see above.
 * @param props.withRoles        Whether the trigger exposes popup-related ARIA roles
 *                               (e.g. aria-haspopup/aria-expanded). Pass `false` when the trigger is
 *                               not currently acting as a popover trigger, so it stops advertising one.
 * @param props.width            Width in px of the dropdown. Defaults to 340.
 * @param props.dataTestIdPrefix Prefix for the test ids: `${dataTestIdPrefix}-confirmation`,
 *                               `${dataTestIdPrefix}-cancel` and `${dataTestIdPrefix}-continue`.
 */
const ConfirmationPopover: FunctionComponent<IProps> = ({
    children,
    opened,
    message,
    onConfirm,
    onCancel,
    onDismiss,
    confirmLabel = "Continue",
    cancelLabel = "Cancel",
    position = "bottom-end",
    withinPortal = false,
    withRoles,
    width = 340,
    dataTestIdPrefix,
}) => {
    return (
        <Popover
            position={position}
            offset={12}
            shadow="md"
            withArrow
            arrowPosition="center"
            arrowSize={15}
            arrowOffset={24}
            withinPortal={withinPortal}
            withRoles={withRoles}
            opened={opened}
            onDismiss={onDismiss}
        >
            <Popover.Target>{children}</Popover.Target>
            {/* The shared Popover wrapper zeroes the dropdown's padding (popover.module.scss), so it
                has to be supplied explicitly here - a consumer that forgets this gets text flush
                against the border. That already shipped once and had to be fixed. */}
            <Popover.Dropdown data-testid={`${dataTestIdPrefix}-confirmation`} p="md" w={width}>
                <Stack gap="sm">
                    <Text size="sm">{message}</Text>
                    <Group gap="sm" justify="flex-end">
                        <Button
                            variant="default"
                            color="gray"
                            size="sm"
                            type="button"
                            onClick={onCancel}
                            data-testid={`${dataTestIdPrefix}-cancel`}
                        >
                            {cancelLabel}
                        </Button>
                        <Button
                            variant="filled"
                            color="blue"
                            size="sm"
                            type="button"
                            onClick={onConfirm}
                            data-testid={`${dataTestIdPrefix}-continue`}
                        >
                            {confirmLabel}
                        </Button>
                    </Group>
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
};

const MemoizedConfirmationPopover = memo(ConfirmationPopover);

export default MemoizedConfirmationPopover;
export { MemoizedConfirmationPopover as ConfirmationPopover };
